import { scheduleWireResourceListCarouselArrows } from '@theme/resource-list-carousel-arrows';

class ProductRecommendations extends HTMLElement {
  /**
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '400px 0px' }
  );

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {boolean} */
  #loadStarted = false;

  connectedCallback() {
    this.#mutationObserver.observe(this, { attributes: true });
    this.#intersectionObserver.observe(this);
    scheduleWireResourceListCarouselArrows(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Initial PDP HTML is skeleton-only. Cards are built when Shopify’s
   * recommendations section endpoint runs (gender + type/category Liquid filter).
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      this.#loadStarted = true;
      this.#intersectionObserver.disconnect();
      this.#mountCards();
      scheduleWireResourceListCarouselArrows(this);
      return;
    }

    if (this.#loadStarted && this.#activeFetch) return;
    this.#loadStarted = true;

    const recIntent = intent || 'related';

    this.#fetchCachedRecommendations(productId, sectionId, recIntent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.#intersectionObserver.disconnect();
          this.innerHTML = recommendations.innerHTML;
          this.#mountCards();
          scheduleWireResourceListCarouselArrows(this);
          requestAnimationFrame(() => {
            scheduleWireResourceListCarouselArrows(this);
          });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  #mountCards() {
    document.dispatchEvent(
      new CustomEvent('pz-collection-cards:mount', { detail: { root: this } })
    );
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    const result = await this.#fetchText(url);
    if (result.success) {
      this.#cachedRecommendations[url] = result.data;
    }
    return result;
  }

  /**
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchText(url) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      /** @type {RequestInit} */
      const fetchInit = { signal: this.#activeFetch.signal, credentials: 'same-origin' };
      try {
        // @ts-ignore
        fetchInit.priority = 'low';
      } catch {
        /* ignore */
      }

      const response = await fetch(url, fetchInit);
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
